/* eslint-disable react/display-name */
/* eslint-disable react/no-children-prop */

import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cx from 'clsx';
import styles from './styles.module.scss';
import { Field, useForm, useFormState } from 'react-final-form';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import InputWrapper from '@/modules/AlphaPWA/Profiles/TradeKey/components/form/inputWrapper';
import FieldText from '@/modules/AlphaPWA/Profiles/TradeKey/components/form/fieldText';
import { composeValidators, required } from '@/utils/form-validate';
import debounce from 'lodash/debounce';
import { checkPresaleTwitterName } from '@/services/presale';
import { WalletContext } from '@/contexts/wallet-context';
import AvatarTwitter from '@/components/AvatarTwitter';
import {
  abbreviateNumber,
  compareString,
  formatCurrency,
  formatName,
} from '@/utils';
import { ITwitterProfile } from '@/interfaces/presale';
import TwitterIcon from '@/modules/AlphaPWA/Profiles/TwitterIcon';
import { ROUTE_PATH } from '@/constants/route-path';
import { useRouter } from 'next/router';
import { closeModal } from '@/state/modal';
import { ISSUE_KEY_MODAL } from '@/modules/AlphaPWA/PlayerShares';
import { useDispatch } from 'react-redux';
import FieldAmount from '../../Profiles/TradeKey/components/form/fieldAmount';
import useDebounce from '@/hooks/useDebounce';
import useTradeKey from '@/hooks/playerShareToken/useTradeKey';
import { formatAmountToClient } from '@/utils/helpers';
import { BigNumber } from 'ethers';
import { AssetsContext } from '@/contexts/assets-context';
import { colors } from '@/utils/color';
import s from './styles.module.scss';

const IssueForm: React.FC<any> = forwardRef(
  ({ handleSubmit, submitting }: any, ref: any) => {
    const [loading, setLoading] = useState(false);
    const { addressL2 } = useContext(WalletContext);
    const { balanceL2 } = useContext(AssetsContext);

    const [twitterProfile, setTwitterProfile] =
      useState<ITwitterProfile | null>(null);
    const { change } = useForm();
    const router = useRouter();
    const dispatch = useDispatch();
    const { values } = useFormState();
    const [hasInput, setHasInput] = useState(false);
    const { estimateTCBeforeMint } = useTradeKey();
    const [error, setError] = useState(false);

    const numberOfKeys = values?.numberOfKeys;
    const amountTC = values?.amountTC || '0';

    const numberOfKeyDebounced = useDebounce(numberOfKeys);

    const isPresale = useMemo(() => {
      return compareString(twitterProfile?.twitter_id, twitterProfile?.address);
    }, [JSON.stringify(twitterProfile)]);

    const onChangeTwitterUserName = (username: any) => {
      setHasInput(true);
      onTwitterUserNameChange({
        username: username,
        address: addressL2,
      });
    };

    const onTwitterUserNameChange = useCallback(
      debounce(p => handleBaseAmountChange(p), 2000),
      []
    );

    useEffect(() => {
      if (numberOfKeys > 0) {
        calcBuyPrice(numberOfKeys);
      } else {
        setError(false);
      }
    }, [numberOfKeyDebounced]);

    const calcBuyPrice = async (numberOfKeys: any) => {
      try {
        const amountTC = await estimateTCBeforeMint({
          supply: 1,
          token_amount: numberOfKeys,
        });
        setError(BigNumber.from(amountTC).gt(balanceL2.amountBTC));
        change('amountTC', amountTC.toString());
      } catch (error) {
        setError(true);
      }
    };

    const handleBaseAmountChange = async ({
      username,
      address,
    }: {
      username: string;
      address: string;
    }) => {
      try {
        setLoading(true);
        setTwitterProfile(null);
        change('twitterProfile', null);
        const res: any = await checkPresaleTwitterName({
          address,
          twitter_name: username,
        });

        setTwitterProfile(res);
        change('twitterProfile', res);
      } catch (err: any) {
        console.log('handleBaseAmountChange err', err);
      } finally {
        setLoading(false);
        setHasInput(false);
      }
    };

    const btnDisable =
      submitting ||
      loading ||
      !twitterProfile ||
      (twitterProfile && twitterProfile.issued) ||
      error;

    const goToTrade = () => {
      if (!twitterProfile?.address) return;
      dispatch(closeModal({ ISSUE_KEY_MODAL }));
      router.push(
        `${ROUTE_PATH.ALPHA_MOBILE_PROFILE}/${twitterProfile?.address}`
      );
    };

    return (
      <form onSubmit={handleSubmit} ref={ref}>
        <InputWrapper className={cx(styles.inputAmountWrap)} theme="light">
          <Field
            name="username"
            children={FieldText}
            validate={composeValidators(required)}
            fieldChanged={onChangeTwitterUserName}
            disabled={submitting || loading}
            placeholder={`Enter their @Twitter username`}
            className={cx(styles.inputAmount)}
            borderColor={'#ECECED'}
            maxlength={50}
          />
        </InputWrapper>
        {loading ? (
          <Flex alignItems={'center'} justifyContent={'center'} mt={'12px'}>
            <Spinner size={'xl'} color="blue.500"></Spinner>
          </Flex>
        ) : (
          <>
            {twitterProfile && (
              <Box mt={6}>
                <Flex
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  px={4}
                  py={3}
                  bgColor={'#FAFAFA'}
                  borderRadius={'8px'}
                  className={s.wrapTwitter}
                >
                  <Flex gap={2} alignItems={'center'}>
                    <AvatarTwitter
                      url={twitterProfile?.twitter_avatar || ''}
                      address={twitterProfile?.twitter_username || ''}
                      width={40}
                    />
                    <Flex direction={'column'} color={'#000000'} gap={2}>
                      <Flex gap={1} alignItems={'center'}>
                        <Text
                          fontSize={'14px'}
                          lineHeight={'18px'}
                          fontWeight={500}
                        >
                          {formatName(twitterProfile?.twitter_name || '', 40)}
                        </Text>
                      </Flex>
                      <Text
                        color={'#000000'}
                        fontSize={'10px'}
                        opacity={'70%'}
                        fontWeight={400}
                      >
                        @{twitterProfile?.twitter_name}
                      </Text>
                    </Flex>
                  </Flex>
                  <Flex
                    gap={2}
                    justifyContent={'flex-end'}
                    color={'#000000'}
                    alignItems={'center'}
                  >
                    <TwitterIcon />
                    <Text fontSize={'14px'} fontWeight={500}>
                      {abbreviateNumber(
                        twitterProfile?.twitter_followers_count
                      )}
                    </Text>
                    <Text
                      fontSize={'10px'}
                      fontWeight={400}
                      color={'rgba(0, 0, 0, 0.6)'}
                    >
                      Followers
                    </Text>
                  </Flex>
                </Flex>

                {twitterProfile?.issued ? (
                  <Flex alignItems={'center'} gap={8} mt={2}>
                    <Text
                      fontSize={'12px'}
                      fontWeight={400}
                      color={'#FF4747'}
                      mt={4}
                    >
                      This Twitter account is already on New Bitcoin City.
                    </Text>
                    <Button
                      className={styles.btnBuyKey}
                      onClick={goToTrade}
                      minW={isPresale ? '170px !important' : '150px !important'}
                    >
                      {isPresale ? 'Buy a placeholder key' : 'Buy a key'}
                    </Button>
                  </Flex>
                ) : (
                  <>
                    <Box mt={4} />
                    <InputWrapper
                      className={cx(styles.inputAmountWrap)}
                      theme="light"
                      label="Wanna buy the first keys?"
                      rightLabel={`Total: ${formatCurrency(
                        formatAmountToClient(amountTC),
                        0,
                        7,
                        'BTC'
                      )} BTC`}
                    >
                      <Field
                        name="numberOfKeys"
                        children={FieldAmount}
                        // fieldChanged={onChangeTwitterUserName}
                        disabled={submitting || loading}
                        className={cx(styles.inputAmount)}
                        borderColor={'#ECECED'}
                        maxLength={3}
                        showError={error}
                        note={
                          error && (
                            <Text
                              color={colors.redPrimary}
                              fontSize={'12px'}
                              style={{
                                color: '#FF4747',
                              }}
                            >
                              Not enough BTC Balance
                            </Text>
                          )
                        }
                      />
                    </InputWrapper>
                  </>
                )}
              </Box>
            )}
            {!!values.username && !twitterProfile && !hasInput && (
              <Text
                fontSize={'12px'}
                fontWeight={400}
                style={{
                  color: '#FF4747',
                }}
                mt={4}
              >
                This account doesn’t exist on Twitter.
              </Text>
            )}
          </>
        )}
        <Flex direction={'column'} mt={8}>
          <Button
            type="submit"
            isLoading={submitting}
            isDisabled={btnDisable}
            loadingText="Processing"
            className={styles.btnSubmit}
          >
            Issue
          </Button>
        </Flex>
      </form>
    );
  }
);

export default IssueForm;
